import React, { useCallback, useMemo, useState } from "react";
import { useDraggable } from "@dnd-kit/react";
import FlagIconCDN from "../../../components/FlagIconCDN/FlagIconCDN";
import StudentIcon from "../../../assets/svg/student-icon";
import ProfessionalIcon from "../../../assets/svg/professional-icon";
import ElectricIcon from "../../../assets/svg/electric-icon";
import LocationIcon from "../../../assets/svg/location-icon";
import RoomTypeIcon from "../../../assets/svg/room-type-icon";
import HomeIcon from "../../../assets/svg/home-icon";
import MoveInIcon from "../../../assets/svg/move-in-icon";
import CreatedDateIcon from "../../../assets/svg/created-date-icon";
import VerbamailIcon from "../../../assets/svg/verbamail-icon";
import VerbamessengerIcon from "../../../assets/svg/verbamessenger-icon";
import VerbacallIcon from "../../../assets/svg/verbacall-icon";
import VerbachatIcon from "../../../assets/svg/verbachat-icon";
import VerbasmsIcon from "../../../assets/svg/verbasms-icon";
import FbMessengerIcon from "../../../assets/svg/fb-messenger-icon";
import WeChatIcon from "../../../assets/svg/wechat-icon";
import InstagramIcon from "../../../assets/svg/instagram-icon";
import CalendarIcon from "../../../assets/svg/calendar-icon";
import ChevronDownIcon from "../../../assets/svg/chevron-down-icon";
import NoteIcon from "../../../assets/svg/note-icon";
import FlaggedIcon from "../../../assets/svg/flagged-icon";
import { Lead } from "../utils/types";
// import { format } from "date-fns";
import { getTagBgColor } from "../../Communications/utils/functions";
import { getCurrencySymbol } from "../../../utils/functions/get-currency-symbols";
import { formatLargeDisplayNumbers } from "../../../utils/functions/formatters";
import { getRoomTypeLabel } from "../../../utils/functions/get-room-type-name";
import { Tooltip } from "../../../components/Tooltip/Tooltip";
// import QrCodeIcon from "../../../assets/svg/qr-code-icon";
import Button from "../../../components/Button/Button";
import SyncStatusIndicator from "../../../components/SyncStatusIndicator/SyncStatusIndicator";
import AgentAssignmentDropdown, {
  AgentUser,
} from "./AgentAssignmentDropdown";
import { useAssignAgentToLeadMutation } from "../../../redux/services/crmApi";
import { customToast } from "../../../utils/custom-toast/custom-toast";
import OutboundArrowIcon from "../../../assets/svg/outbound-arrow-icon";
import { resetEmailEditor } from "../../../redux/slices/emailEditorSlice";
import { trackEvents } from "../../../utils/amplitude/amplitude";
import { useDispatch } from "react-redux";
import { useGetClientObjectFromList } from "../../../utils/functions/client-helpers";
import {
  openComposeEmailModal,
  setSelectedTab,
  saveDraftForLead,
} from "../../../redux/slices/composeEmailModalSlice";
import { useTwilioCallHandler } from "../../../hooks/use-twilio-call-handler";
import CallLeadConfirmationModal from "./LeadDetails/components/CallLeadConfirmationModal";
import InfoBtnIcon from "../../../assets/svg/info-btn-icon";
import { getDateTimeByFormat } from "../../../redux/slices/dateTimeFormatSlice";
import {
  getDurationDisplayValue,
  getLeadTagDisplayName,
  getLostReasonDisplayName,
  getTaskLabels,
} from "../../../utils/constants/mappings";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";

const getSourceChannelIcon = (channel: string) => {
  switch (channel) {
    case "email":
      return (
        <Tooltip content="VerbaMail">
          <span>
            <VerbamailIcon className="shrink-0 w-[12px] h-[10px] text-verba-mail dark:text-verba-mail-dk" />
          </span>
        </Tooltip>
      );
    case "whatsapp":
      return (
        <Tooltip content="VerbaMessenger">
          <span>
            <VerbamessengerIcon className="shrink-0 w-[12px] h-[10px] text-verba-messenger dark:text-verba-messenger-dk" />
          </span>
        </Tooltip>
      );
    case "call":
      return (
        <Tooltip content="VerbaCall">
          <span>
            <VerbacallIcon className="shrink-0 w-[12px] h-[10px] text-verba-lavender dark:text-verba-lavender-dk" />
          </span>
        </Tooltip>
      );
    case "widget":
      return (
        <Tooltip content="VerbaChat">
          <span>
            <VerbachatIcon className="shrink-0 w-[12px] h-[10px] text-verba-blue dark:text-verba-blue-dk" />
          </span>
        </Tooltip>
      );
    case "sms":
      return (
        <Tooltip content="VerbaSMS">
          <span>
            <VerbasmsIcon className="shrink-0 w-[12px] h-[10px] text-verba-sms dark:text-verba-sms-dk" />
          </span>
        </Tooltip>
      );
    case "fb_messenger":
      return (
        <Tooltip content="Facebook Messenger">
          <span>
            <FbMessengerIcon className="shrink-0 w-[12px] h-[10px] text-verba-fb-messenger dark:text-verba-fb-messenger-dk" />
          </span>
        </Tooltip>
      );
    case "wechat":
      return (
        <Tooltip content="WeChat">
          <span>
            <WeChatIcon className="shrink-0 w-[12px] h-[10px]" />
          </span>
        </Tooltip>
      );
    case "instagram":
      return (
        <Tooltip content="Instagram DMs">
          <span>
            <InstagramIcon className="shrink-0 w-[12px] h-[10px]" />
          </span>
        </Tooltip>
      );
    default:
      return null;
  }
};

const LeadKanbanCard = ({
  lead,
  onSelect,
  stageId,
  handleLeadUpdate,
}: {
  lead: Lead;
  onSelect: (leadId: string, modality?: string) => void;
  stageId: string;
  handleLeadUpdate: (lead: Lead, stageId: string) => void;
}) => {
  const dispatch = useDispatch();

  const client = useSelector((state: RootState) => state?.auth?.client);
  const stages = useSelector((state: RootState) => state.crmLeadList.stages);
  const taskLabels = getTaskLabels(client?.client_code === "verbaflo");
  const isLostStage = lead?.current_stage === "lost" || stages?.[lead?.current_stage]?.is_lost === true;

  const leadAny = lead as unknown as Record<string, unknown>;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isCallConfirmationOpen, setIsCallConfirmationOpen] = useState(false);

  const [assignAgentToLead, { isLoading: isAssigningAgent }] =
    useAssignAgentToLeadMutation();

  const clientForOrgView = useGetClientObjectFromList(lead?.client_code || "");
  const {
    client_logo: clientLogoForOrgView,
    client_name: clientNameForOrgView,
  } = clientForOrgView || {};

  const { handleMakeCall } = useTwilioCallHandler(
    lead?.lead_id || "",
    "lead",
    {
      lead: {
        country_code: lead?.phone_country_code || "",
        phone: lead?.phone,
      },
    },
    // Format the dialer number with country code if both are present
    lead?.phone && lead?.phone_country_code
      ? (lead?.phone_country_code.startsWith("+")
          ? lead?.phone_country_code
          : `+${lead?.phone_country_code}`) + lead?.phone
      : undefined
  );

  const handleVerbaCallClick = () => {
    setIsCallConfirmationOpen(true);
  };

  const handleAssignAgent = useCallback(
    async (agentIds: string[], agents: AgentUser[]) => {
      const previousAgentId = lead?.agent?.[0]?.id || null;
      const previousAgentName = lead?.agent?.[0]?.name || "";
      try {
        await assignAgentToLead({
          leadId: lead.lead_id,
          agentIds,
        }).unwrap();
        const updatedLead = {
          ...lead,
          agent: agents.map((a) => ({
            id: a.id,
            name: a.name,
            role: a.role,
            status: a.status,
            profile_picture: a.profile_picture,
          })),
        } as Lead;
        handleLeadUpdate(updatedLead, stageId);

        const isReassignment = !!previousAgentId;
        trackEvents(isReassignment ? "agent_reassigned" : "agent_assigned", {
          lead_id: lead.lead_id,
          lead_name: lead?.name || "",
          channel: lead?.tracking_info?.source_channel || "",
          assigned_agent_id: agentIds[0] || "",
          assigned_agent_name: agents[0]?.name || "",
          ...(isReassignment && {
            previous_agent_id: previousAgentId,
            previous_agent_name: previousAgentName,
          }),
          module: "crm",
          timestamp: new Date().toISOString(),
        });

        customToast(
          `${agentIds?.length > 1 ? "Agents" : "Agent"} assigned successfully`,
          "success"
        );
      } catch (error) {
        console.error(error);
        customToast("Failed to assign agent", "error");
      }
    },
    [assignAgentToLead, handleLeadUpdate, lead, stageId]
  );

  const handleEmailBuilderClick = (startTab?: string) => {
    dispatch(resetEmailEditor());
    dispatch(saveDraftForLead());
    dispatch(
      openComposeEmailModal({
        title: "New Email",
        viewState: startTab ? "expanded" : "mini",
        leadId: lead.lead_id,
        leadEmail: lead?.email || "",
        templateVariableMapping: lead?.template_variable_mapping,
      })
    );
    if (startTab) {
      dispatch(setSelectedTab(startTab));
    }
  };

  const handleScrollToggle = useCallback((lock: boolean) => {
    const containers = document.querySelectorAll(
      "[data-crm-stage-scroll-container]"
    );
    containers.forEach((el) => {
      (el as HTMLElement).style.overflowY = lock ? "hidden" : "auto";
      (el as HTMLElement).style.overflowX = "hidden";
    });
  }, []);

  const defaultSelectedAgents = useMemo(() => {
    if (!Array.isArray(lead?.agent) || lead.agent.length === 0) return [];
    return lead.agent.map((a) => ({
      id: a.id,
      name: a.name,
      role: a.role,
      status: a.status,
      profile_picture: a.profile_picture || null,
      email: a.email || "",
    }));
  }, [lead?.agent]);

  const visibleTime = lead?.created_at || "";

  const { ref: draggableRef } = useDraggable({
    id: `lead-${lead.lead_id}`,
    data: {
      type: "lead",
      leadId: lead.lead_id,
      fromStageId: stageId,
    },
  });

  return (
    <div
      ref={draggableRef}
      onClick={() => onSelect(lead.lead_id)}
      className="group w-[300px] cursor-grab active:cursor-grabbing flex flex-col gap-[8px] border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk rounded-[8px] p-[12px] hover:border-2 hover:border-crm-card-border dark:hover:border-crm-card-border-dk transition-colors ease-in-out duration-300 hover:bg-crm-card-highlight dark:hover:bg-crm-card-highlight-dk"
    >
      {(lead?.source_country_alpha2 ||
        lead?.name ||
        lead?.tenant_type === "student" ||
        lead?.tenant_type === "working professional" ||
        lead?.lead_score ||
        lead?.budget?.min_budget ||
        lead?.budget?.max_budget) && (
        <div className="flex items-center gap-[16px] min-w-0">
          <div className="flex-1 flex items-center gap-[4px] min-w-0">
            {lead?.source_country_alpha2 && (
              <Tooltip
                key="source country"
                content={`Source Country: ${lead?.source_country}`}
                side="top"
              >
                <div className="w-[18px] h-[14px] shrink-0">
                  <FlagIconCDN
                    code={lead?.source_country_alpha2}
                    flagIconClassName="w-[18px] h-[14px] shrink-0 border border-gray-75 dark:border-gray-75-dk rounded-[2px]"
                  />
                </div>
              </Tooltip>
            )}

            {lead?.name && (
              <Tooltip key="lead name" content={lead?.name} side="top">
                <p className="max-w-fit truncate text-md text-verba-indigo dark:text-verba-indigo-dk font-medium">
                  {lead?.name}
                </p>
              </Tooltip>
            )}
          </div>

          <div className="flex items-center gap-[4px] shrink-0">
            {(() => {
              const elements = [];

              if (lead?.tenant_type === "student") {
                elements.push(
                  <Tooltip key="student" content="Student" side="top">
                    <div className="w-[12px] h-[12px] shrink-0">
                      <StudentIcon className="shrink-0 w-[12px] h-[12px] text-verba-mail dark:text-verba-mail-dk" />
                    </div>
                  </Tooltip>
                );
              }

              if (lead?.tenant_type === "working professional") {
                elements.push(
                  <Tooltip
                    key="professional"
                    content="Working Professional"
                    side="top"
                  >
                    <div className="w-[12px] h-[12px] shrink-0">
                      <ProfessionalIcon className="shrink-0 w-[12px] h-[12px] text-analytics-safety dark:text-analytics-safety-dk" />
                    </div>
                  </Tooltip>
                );
              }

              if (lead?.lead_score) {
                elements.push(
                  <Tooltip key="lead score" content="Lead Score" side="top">
                    <div key="score" className="flex items-center">
                      <ElectricIcon className="shrink-0 w-[10px] h-[10px] text-verba-mail dark:text-verba-mail-dk" />
                      <p className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk">
                        {lead?.lead_score}
                      </p>
                    </div>
                  </Tooltip>
                );
              }

              if (lead?.budget?.min_budget || lead?.budget?.max_budget) {
                elements.push(
                  <div className="flex items-center gap-[2px]">
                    <Tooltip key="budget" content="Budget" side="top">
                      <div className="max-w-fit flex items-center gap-px">
                        <p className="text-xs font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                          {getCurrencySymbol(lead?.budget?.currency)}
                          {formatLargeDisplayNumbers(
                            lead?.budget?.min_budget ?? 0
                          )}
                        </p>
                        {lead?.budget?.max_budget >= 0 &&
                          lead?.budget?.min_budget >= 0 && (
                            <p className="text-xs font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                              -
                            </p>
                          )}
                        <p className="text-xs font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                          {getCurrencySymbol(lead?.budget?.currency)}
                          {formatLargeDisplayNumbers(
                            lead?.budget?.max_budget ?? 0
                          )}
                        </p>
                      </div>
                    </Tooltip>
                    <p className="text-md font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
                      /
                      {getDurationDisplayValue(lead?.budget?.duration) ||
                        "week"}
                    </p>
                  </div>
                );
              }

              return elements.map((element, index) => (
                <React.Fragment key={index}>
                  {element}
                  {index < elements.length - 1 && (
                    <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
                  )}
                </React.Fragment>
              ));
            })()}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-[4px]">
        {Boolean(leadAny?.company_name) && (
          <Tooltip key="company name" content="Company Name" side="top">
            <div className="max-w-fit flex items-center gap-[4px]">
              <ProfessionalIcon className="shrink-0 w-[12px] h-[12px] text-gray-275 dark:text-gray-275-dk" />
              <p className="truncate text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                {String(leadAny.company_name)}
              </p>
            </div>
          </Tooltip>
        )}
        {Boolean(leadAny?.number_of_beds) && (
          <Tooltip key="beds" content="Beds" side="top">
            <div className="max-w-fit flex items-center gap-[4px]">
              <RoomTypeIcon className="shrink-0 w-[12px] h-[12px] text-gray-275 dark:text-gray-275-dk" />
              <p className="truncate text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                {(leadAny.number_of_beds as { min: number; max: number })
                  .min ===
                (leadAny.number_of_beds as { min: number; max: number }).max
                  ? (leadAny.number_of_beds as { min: number; max: number }).min
                  : `${(leadAny.number_of_beds as { min: number; max: number }).min} - ${(leadAny.number_of_beds as { min: number; max: number }).max}`}
              </p>
            </div>
          </Tooltip>
        )}
        {Boolean(leadAny?.number_of_properties) && (
          <Tooltip key="properties" content="Properties" side="top">
            <div className="max-w-fit flex items-center gap-[4px]">
              <HomeIcon className="shrink-0 w-[12px] h-[12px] text-gray-275 dark:text-gray-275-dk" />
              <p className="truncate text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                {(leadAny.number_of_properties as { min: number; max: number })
                  .min ===
                (leadAny.number_of_properties as { min: number; max: number })
                  .max
                  ? (
                      leadAny.number_of_properties as {
                        min: number;
                        max: number;
                      }
                    ).min
                  : `${(leadAny.number_of_properties as { min: number; max: number }).min} - ${(leadAny.number_of_properties as { min: number; max: number }).max}`}
              </p>
            </div>
          </Tooltip>
        )}
        {Boolean(leadAny?.industry) && (
          <Tooltip key="industry" content="Industry" side="top">
            <div className="max-w-fit flex items-center gap-[4px]">
              <ElectricIcon className="shrink-0 w-[12px] h-[12px] text-gray-275 dark:text-gray-275-dk" />
              <p className="truncate text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk capitalize">
                {String(leadAny.industry).replace(/_/g, " ")}
              </p>
            </div>
          </Tooltip>
        )}
        {Boolean(leadAny?.verbaflo_request_priority) && (
          <Tooltip key="priority" content="Priority" side="top">
            <div className="max-w-fit flex items-center gap-[4px]">
              <FlaggedIcon className="shrink-0 w-[12px] h-[12px] text-gray-275 dark:text-gray-275-dk" />
              <p className="truncate text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk capitalize">
                {String(leadAny.verbaflo_request_priority)}
              </p>
            </div>
          </Tooltip>
        )}
        {lead?.destination_city && (
          <Tooltip key="destination city" content="Destination City" side="top">
            <div className="max-w-fit flex items-center gap-[4px]">
              <LocationIcon className="shrink-0 w-[12px] h-[12px] text-gray-275 dark:text-gray-275-dk" />
              <p className="truncate text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                {lead?.destination_city}
              </p>
            </div>
          </Tooltip>
        )}
        {lead?.move_in_date && (
          <Tooltip key="move in date" content="Move-in Date" side="top">
            <div className="max-w-fit flex items-center gap-[4px]">
              <MoveInIcon className="shrink-0 w-[12px] h-[12px] text-gray-275 dark:text-gray-275-dk" />
              <p className="truncate text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                {getDateTimeByFormat(new Date(lead?.move_in_date))?.date}
              </p>
            </div>
          </Tooltip>
        )}
        {lead?.property && Array.isArray(lead?.property) && (
          <Tooltip
            key="selected property"
            content="Selected Property"
            side="top"
          >
            <div className="max-w-fit flex items-center gap-[4px]">
              <HomeIcon className="shrink-0 w-[12px] h-[12px] text-gray-275 dark:text-gray-275-dk" />
              <p className="truncate text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                {lead?.property?.join(", ")}
              </p>
            </div>
          </Tooltip>
        )}
        {lead?.room_type &&
          Array.isArray(lead.room_type) &&
          lead.room_type.length > 0 && (
            <Tooltip key="room type" content="Room Type" side="top">
              <div className="max-w-fit flex items-center gap-[4px]">
                <RoomTypeIcon className="shrink-0 w-[12px] h-[12px] text-gray-275 dark:text-gray-275-dk" />
                <p className="truncate text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                  {lead.room_type
                    .map((room) => getRoomTypeLabel(room))
                    .join(", ")}
                </p>
              </div>
            </Tooltip>
          )}
        {visibleTime && (
          <Tooltip key="created on" content={"Created On"} side="top">
            <div className="max-w-fit flex items-center gap-[4px]">
              <CalendarIcon className="shrink-0 w-[12px] h-[12px] text-gray-275 dark:text-gray-275-dk" />
              <p className="truncate text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                {getDateTimeByFormat(new Date(visibleTime))?.date},{" "}
                {getDateTimeByFormat(new Date(visibleTime))?.time}
              </p>
            </div>
          </Tooltip>
        )}
        {lead?.tracking_info?.source_channel && (
          <div className="flex items-center gap-[4px] isolate">
            <Tooltip content="Created With">
              <span className="shrink-0">
                <CreatedDateIcon className="w-[12px] h-[12px] text-gray-275 dark:text-gray-275-dk" />
              </span>
            </Tooltip>
            <div className="flex items-center gap-[4px]">
              {getSourceChannelIcon(lead?.tracking_info?.source_channel)}
            </div>
          </div>
        )}
        {lead?.has_outbound_messages && (
          <Tooltip
            content={
              <p className="flex items-center gap-[4px]">
                <span className="font-semibold text-sm">WorkFlo:</span>
                <span className="text-xs">
                  {lead?.campaign_metadata &&
                    lead?.campaign_metadata?.length > 0 &&
                    `${lead?.campaign_metadata[0]?.campaign_name}`}
                </span>
                {lead?.campaign_metadata?.length > 1 && (
                  <span className="text-xxs font-semibold bg-dark-gray dark:bg-dark-gray-dk rounded-[4px] px-[4px] py-[2px]">
                    +{lead?.campaign_metadata?.length - 1}
                  </span>
                )}
              </p>
            }
            contentClassName="max-w-fit text-md"
          >
            <span className="shrink-0 max-w-fit">
              <OutboundArrowIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
            </span>
          </Tooltip>
        )}
      </div>

      {isLostStage &&
        (lead?.lost_primary_reason || lead?.lost_reason) && (
          <div className="flex items-center gap-[4px]">
            <p className="shrink-0 text-xs font-semibold text-verba-indigo dark:text-verba-indigo-dk">
              Lost Reason:
            </p>
            {lead?.lost_primary_reason && (
              <Tooltip content="Primary Lost Reason" side="top">
                <p className="truncate w-fit max-w-[105px] shrink-0 px-[4px] py-[2px] rounded-[4px] border border-gray-100 dark:border-gray-100-dk text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                  {getLostReasonDisplayName(
                    lead?.lost_primary_reason,
                    client?.client_code
                  )}
                </p>
              </Tooltip>
            )}
            {lead?.lost_reason && (
              <Tooltip content="Secondary Lost Reason" side="top">
                <p className="truncate w-fit max-w-[105px] shrink-0 px-[4px] py-[2px] rounded-[4px] border border-gray-100 dark:border-gray-100-dk text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                  {getLostReasonDisplayName(
                    lead?.lost_reason,
                    client?.client_code
                  )}
                </p>
              </Tooltip>
            )}
          </div>
        )}

      {lead?.current_stage === "cancelled" &&
        (lead?.cancelled_primary_reason || lead?.cancelled_reason) && (
          <div className="flex items-center gap-[4px]">
            <p className="shrink-0 text-xs font-semibold text-verba-indigo dark:text-verba-indigo-dk">
              Reason:
            </p>
            {lead?.cancelled_primary_reason && (
              <Tooltip content="Primary Cancellation Reason" side="top">
                <p className="truncate w-fit max-w-[105px] shrink-0 px-[4px] py-[2px] rounded-[4px] border border-gray-100 dark:border-gray-100-dk text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                  {getLostReasonDisplayName(
                    lead?.cancelled_primary_reason,
                    client?.client_code
                  )}
                </p>
              </Tooltip>
            )}
            {lead?.cancelled_reason && (
              <Tooltip content="Secondary Cancellation Reason" side="top">
                <p className="truncate w-fit max-w-[105px] shrink-0 px-[4px] py-[2px] rounded-[4px] border border-gray-100 dark:border-gray-100-dk text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk">
                  {getLostReasonDisplayName(
                    lead?.cancelled_reason,
                    client?.client_code
                  )}
                </p>
              </Tooltip>
            )}
          </div>
        )}

      {(lead?.original_lead_id || (lead?.tags && lead?.tags.length > 0)) && (
        <div className="flex flex-wrap items-center gap-[4px]">
          {lead?.original_lead_id && (
            <div className="w-fit shrink-0 bg-tag-repeat-lead dark:bg-tag-repeat-lead-dk rounded-[4px] px-[6px] py-[2px] flex items-center">
              <InfoBtnIcon className="w-4 h-4 text-fixed-white dark:text-fixed-white-dk" />
              <p className="text-xxs font-semibold text-fixed-white dark:text-fixed-white-dk">
                Repeat Lead
              </p>
            </div>
          )}
          {lead?.original_lead_id && lead?.tags && lead?.tags.length > 0 && (
            <div className="shrink-0 bg-border-gray dark:bg-border-gray-dk w-px h-[10px] mx-[4px]"></div>
          )}

          {lead?.tags &&
            lead?.tags.length > 0 &&
            lead?.tags
              .slice(0, isExpanded ? lead?.tags.length : 4)
              .map((tag, index) => (
                <div
                  key={index}
                  className={`w-fit shrink-0 px-[4px] py-[2px] rounded-[4px] border border-gray-100 dark:border-gray-100-dk ${getTagBgColor(
                    tag.colour
                  )}`}
                >
                  <p className="text-xxs text-heading-text dark:text-heading-text-dk font-medium">
                    {getLeadTagDisplayName(tag.name, client?.client_code)}
                  </p>
                </div>
              ))}

          {lead?.tags && lead?.tags.length > 4 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsExpanded(!isExpanded);
              }}
              className="underline underline-offset-2 flex items-center gap-1 text-xxs text-verba-indigo dark:text-verba-indigo-dk font-medium"
            >
              <span>
                {isExpanded ? `Show less` : `+${lead?.tags.length - 4} more`}
              </span>

              <ChevronDownIcon
                className={`shrink-0 w-2 h-2 text-verba-indigo dark:text-verba-indigo-dk transition-transform ${
                  isExpanded ? "rotate-180" : ""
                }`}
              />
            </button>
          )}
        </div>
      )}

      {(lead?.notes_count > 0 ||
        lead?.total_tasks_count > 0 ||
        lead?.session_item_count?.call > 0 ||
        lead?.session_item_count?.widget > 0 ||
        lead?.session_item_count?.email > 0 ||
        lead?.session_item_count?.whatsapp > 0 ||
        lead?.session_item_count?.sms > 0) && (
        <div className="flex flex-wrap items-center gap-[4px]">
          {lead?.notes_count > 0 && (
            <div className="flex shrink-0 items-center gap-[2px] border border-gray-100 dark:border-gray-100-dk rounded-[4px] px-[4px] py-[2px]">
              <NoteIcon className="shrink-0 w-[10px] h-[10px] text-verba-mail dark:text-verba-mail-dk" />
              <p className="text-heading-text dark:text-heading-text-dk font-semibold text-xxs">
                {lead?.notes_count}
              </p>
              <p className="text-heading-text dark:text-heading-text-dk font-normal text-xxs">
                Notes
              </p>
            </div>
          )}

          {lead?.total_tasks_count > 0 && (
            <div className="flex shrink-0 items-center gap-[2px] bg-tag-red-bg dark:bg-tag-red-bg-dk border border-tag-repeat-lead dark:border-tag-repeat-lead-dk rounded-[4px] px-[4px] py-[2px]">
              <FlaggedIcon className="shrink-0 w-[10px] h-[10px] text-verba-red-alert dark:text-verba-red-alert-dk" />
              <p className="text-heading-text dark:text-heading-text-dk font-semibold text-xxs">
                {lead?.total_tasks_count}
              </p>
              <p className="text-heading-text dark:text-heading-text-dk font-normal text-xxs">
                {taskLabels.plural}{" "}
                {lead?.overdue_tasks_count && lead?.overdue_tasks_count > 0
                  ? `(${lead?.overdue_tasks_count} Overdue)`
                  : ""}
              </p>
            </div>
          )}

          {lead?.session_item_count?.call > 0 && (
            <Tooltip
              key="Total number of calls"
              content={"Total No. of Calls"}
              side="top"
            >
              <div className="max-w-fit flex shrink-0 items-center gap-[2px] border border-gray-100 dark:border-gray-100-dk rounded-[4px] px-[4px] py-[2px]">
                <VerbacallIcon className="shrink-0 w-[10px] h-[10px] text-verba-lavender dark:text-verba-lavender-dk" />
                <p className="text-heading-text dark:text-heading-text-dk font-semibold text-xxs">
                  {lead?.session_item_count?.call}
                </p>
              </div>
            </Tooltip>
          )}

          {lead?.session_item_count?.widget > 0 && (
            <Tooltip
              key="Chatbot sessions"
              content={"Chatbot Sessions"}
              side="top"
            >
              <div className="max-w-fit flex shrink-0 items-center gap-[2px] border border-gray-100 dark:border-gray-100-dk rounded-[4px] px-[4px] py-[2px]">
                <VerbachatIcon className="shrink-0 w-[10px] h-[10px] text-verba-blue dark:text-verba-blue-dk" />
                <p className="text-heading-text dark:text-heading-text-dk font-semibold text-xxs">
                  {lead?.session_item_count?.widget}
                </p>
              </div>
            </Tooltip>
          )}

          {lead?.session_item_count?.email > 0 && (
            <Tooltip
              key="Total number of emails"
              content={"Total Emails Sent"}
              side="top"
            >
              <div className="max-w-fit flex shrink-0 items-center gap-[2px] border border-gray-100 dark:border-gray-100-dk rounded-[4px] px-[4px] py-[2px]">
                <VerbamailIcon className="shrink-0 w-[10px] h-[10px] text-verba-mail dark:text-verba-mail-dk" />
                <p className="text-heading-text dark:text-heading-text-dk font-semibold text-xxs">
                  {lead?.session_item_count?.email}
                </p>
              </div>
            </Tooltip>
          )}

          {lead?.session_item_count?.whatsapp > 0 && (
            <Tooltip
              key="Whatsapp sessions"
              content={"Whatsapp Sessions"}
              side="top"
            >
              <div className="max-w-fit flex shrink-0 items-center gap-[2px] border border-gray-100 dark:border-gray-100-dk rounded-[4px] px-[4px] py-[2px]">
                <VerbamessengerIcon className="shrink-0 w-[10px] h-[10px] text-verba-messenger dark:text-verba-messenger-dk" />
                <p className="text-heading-text dark:text-heading-text-dk font-semibold text-xxs">
                  {lead?.session_item_count?.whatsapp}
                </p>
              </div>
            </Tooltip>
          )}

          {lead?.session_item_count?.sms > 0 && (
            <Tooltip
              key="SMS messages"
              content={"Total SMS Messages"}
              side="top"
            >
              <div className="max-w-fit flex shrink-0 items-center gap-[2px] border border-gray-100 dark:border-gray-100-dk rounded-[4px] px-[4px] py-[2px]">
                <VerbasmsIcon className="shrink-0 w-[10px] h-[10px] text-verba-sms dark:text-verba-sms-dk" />
                <p className="text-heading-text dark:text-heading-text-dk font-semibold text-xxs">
                  {lead?.session_item_count?.sms}
                </p>
              </div>
            </Tooltip>
          )}

          {lead?.session_item_count.fb_messenger > 0 && (
            <Tooltip
              key="FB Messenger messages"
              content={"Total Messenger Messages"}
              side="top"
            >
              <div className="max-w-fit flex shrink-0 items-center gap-[2px] border border-gray-100 dark:border-gray-100-dk rounded-[4px] px-[4px] py-[2px]">
                <FbMessengerIcon className="shrink-0 w-[10px] h-[10px] text-verba-fb-messenger dark:text-verba-fb-messenger-dk" />
                <p className="text-heading-text dark:text-heading-text-dk font-semibold text-xxs">
                  {lead?.session_item_count?.fb_messenger}
                </p>
              </div>
            </Tooltip>
          )}

          {lead?.session_item_count?.instagram > 0 && (
            <Tooltip
              key="Instagram DMs messages"
              content={"Total Instagram DMs Messages"}
              side="top"
            >
              <div className="max-w-fit flex shrink-0 items-center gap-[2px] border border-gray-100 dark:border-gray-100-dk rounded-[4px] px-[4px] py-[2px]">
                <InstagramIcon className="shrink-0 w-[10px] h-[10px]" />
                <p className="text-heading-text dark:text-heading-text-dk font-semibold text-xxs">
                  {lead?.session_item_count?.instagram}
                </p>
              </div>
            </Tooltip>
          )}
        </div>
      )}
      {lead?.last_contacted_at && (
        <div className="flex items-center justify-between bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-md px-2 py-1.5">
          <p className="text-[10px] font-normal text-verba-indigo dark:text-verba-indigo-dk">
            Last Contacted
          </p>
          <p className="text-[10px] font-normal text-verba-indigo dark:text-verba-indigo-dk">
            {getDateTimeByFormat(new Date(lead.last_contacted_at))?.date},{" "}
            {getDateTimeByFormat(new Date(lead.last_contacted_at))?.time}
          </p>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <AgentAssignmentDropdown
          selectedAgentIds={
            Array.isArray(lead?.agent) ? lead.agent.map((a) => a.id) : []
          }
          onApply={handleAssignAgent}
          isLoading={isAssigningAgent}
          maxAgents={2}
          onToggleScroll={handleScrollToggle}
          defaultSelectedAgents={defaultSelectedAgents}
          triggerMaxWidth="max-w-[190px]"
        />

        <CallLeadConfirmationModal
          open={isCallConfirmationOpen}
          onOpenChange={setIsCallConfirmationOpen}
          onConfirm={handleMakeCall}
          leadName={lead?.name || "Unknown"}
        />

        <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center">
          {/* <Button
            Icon={QrCodeIcon}
            buttonContainerClass="w-fit p-[5.3px] rounded-[5.3px] hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white text-verba-indigo dark:text-verba-indigo-dk transition-colors duration-200"
            iconClass="w-4 h-4"
          /> */}
          {lead?.whatsapp_phone && lead?.whatsapp_phone_country_code && (
            <Button
              Icon={VerbamessengerIcon}
              buttonContainerClass="w-fit p-[5.3px] rounded-[5.3px] hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white text-verba-indigo dark:text-verba-indigo-dk transition-colors duration-200"
              iconClass="w-4 h-4"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(lead?.lead_id, "whatsapp");
              }}
            />
          )}
          {lead?.phone && lead?.phone_country_code && (
            <Button
              Icon={VerbacallIcon}
              onClick={(e) => {
                e.stopPropagation();
                handleVerbaCallClick();
              }}
              buttonContainerClass="w-fit p-[5.3px] rounded-[5.3px] hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white text-verba-indigo dark:text-verba-indigo-dk transition-colors duration-200"
              iconClass="w-4 h-4"
            />
          )}
          {lead?.email && (
            <Button
              Icon={VerbamailIcon}
              onClick={(e) => {
                e.stopPropagation();
                handleEmailBuilderClick();
              }}
              buttonContainerClass="w-fit p-[5.3px] rounded-[5.3px] hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:text-fixed-white text-verba-indigo dark:text-verba-indigo-dk transition-colors duration-200"
              iconClass="w-4 h-4"
            />
          )}
        </div>
      </div>

      {lead?.sync_status && (
        <SyncStatusIndicator
          syncStatus={lead?.sync_status}
          lastSyncedAt={lead?.last_synced_at}
        />
      )}
    </div>
  );
};

export default LeadKanbanCard;
